/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React, { useState } from "react";
import { Button, ButtonProps, Menu, MenuItem, styled, Typography } from "@mui/material";
import { AngleDownIcon, EarthIcon } from "../../FontAwesome";
import { translationKeys, useTranslation } from "../../Internationalisierung/i18nBasis";
import { countries, cultures, languages } from "../../Internationalisierung/languages";
import StringUtil from "../../Utils/StringUtil";

type Kultur = {
    sprachcode: string;
    kultur: string;
}

function ermittleAktuellesLand() {
    const laenderCode = window.navigator.language;
    const land = Object.values(cultures).find(land => StringUtil.equalsIgnoreCase(laenderCode, land));
    return land?.split("-")[1].toUpperCase() ?? countries.gb;
}

function ermittleKultur(sprachcode: string, land: string): Kultur {
    let laendercode = countries.gb;
    sprachcode = sprachcode.toLowerCase();
    land = land.toUpperCase();
    if (sprachcode === languages.en) {
        laendercode = Object.values(countries).includes(land) ? land : countries.gb;
    } else if (sprachcode === languages.de) {
        laendercode = [countries.de, countries.ch].includes(land) ? land : countries.de;
    } else {
        sprachcode = languages.en;
        laendercode = countries.gb;
    }

    return {
        kultur: `${sprachcode}-${laendercode}`,
        sprachcode: sprachcode
    };
}

function ermittleInitialeSprache(sprachcodes: string[], initialeSprache?: string): string {
    // Kann "de" oder auch "de-DE" liefern. Siehe https://developer.mozilla.org/en-US/docs/Web/API/Navigator/language
    const sprachenCode = initialeSprache ?? window.navigator.language;
    const sprache = sprachcodes.find(sprachcode => StringUtil.startsWithIgnoreCase(sprachenCode, sprachcode));
    return sprache ?? languages.en;
}

const Sprachcode = styled(Typography)(({ theme }) => ({
    fontWeight: "bold", fontSize: "0.75rem", mt: ".25rem", mb: ".25rem"
}));

export default function SpracheSelect(props: {
    buttonProps?: ButtonProps,
    onChange: (kultur: Kultur) => void,
    initialeSprache?: string
}) {
    const sprachcodes: string[] = Object.values(languages);
    const [sprachcode, setSprachcode] = useState(ermittleInitialeSprache(sprachcodes, props.initialeSprache));
    const { t, i18n } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = !!anchorEl;
    const land = ermittleAktuellesLand();

    React.useEffect(() => {
        if (props.initialeSprache) {
            setSprachcode(ermittleInitialeSprache(sprachcodes, props.initialeSprache))
        }
    }, [props.initialeSprache])

    const handleMenuClose = () => {
        setAnchorEl(null)
    }

    const handleMenuItemClick = (sprachcode: typeof sprachcodes[number]) => () => {
        setSprachcode(sprachcode);
        const kultur = ermittleKultur(sprachcode, land);
        props.onChange(kultur);
        handleMenuClose();
    }

    const handleButtonClick = (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setAnchorEl(evt.currentTarget);
    }

    const ermittleSprachcodeName = (sprachcode: string) =>
        t(translationKeys.portalSpracheSelect[sprachcode as keyof typeof translationKeys.portalSpracheSelect])
        + " - "
        + sprachcode.toUpperCase()

    return (
        <>
            <Button
                {...props.buttonProps}
                onClick={handleButtonClick}
                startIcon={<EarthIcon sx={{ mr: "-2px" }} />}
                endIcon={<AngleDownIcon sx={{ ml: "-2px" }} />}
            >
                <Sprachcode>{sprachcode}</Sprachcode>
            </Button>
            <Menu
                open={open}
                onClose={handleMenuClose}
                anchorEl={anchorEl}
            >
                <Sprachcode sx={{ color: "#909090", mb: ".5rem" }}>
                    {t(translationKeys.portalSpracheSelect.sprache)}
                </Sprachcode>
                {sprachcodes.map(sprachcode => (
                    <MenuItem
                        key={sprachcode}
                        onClick={handleMenuItemClick(sprachcode)}
                        selected={(!props.initialeSprache && i18n.language.toLowerCase().startsWith(sprachcode)) ||
                            (!!props.initialeSprache && props.initialeSprache.startsWith(sprachcode))}
                    >
                        {ermittleSprachcodeName(sprachcode)}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}